
import React, { useState, useContext, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../axiosInstance';
import { LanguageContext } from '../LanguageContext';
import useWafCaptcha from '../hooks/useWafCaptcha';

function ForgotPasswordRequest() {
  const { language } = useContext(LanguageContext);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const successRef = useRef(null);
  const errorRef = useRef(null);
  const { ensureCaptcha, CaptchaOverlay } = useWafCaptcha();

  useEffect(() => {
    if (submitted && successRef.current) {
      successRef.current.focus();
    }
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [submitted, error]);

  const text = {
    en: {
      skip: 'Skip to main content',
      heading: 'Forgot your password?',
      intro: "Enter the email address used for your account. If it matches an account, we will send a verification code so you can reset your password.",
      email: 'Email address',
      submit: 'Send verification code',
      successHeadline: 'If an account with that email exists, a verification code has been sent.',
      successDetail: 'When the code arrives, you can continue to the reset page using the button below.',
      continue: 'Continue to reset password',
      error: 'Something went wrong. Please try again later.'
    },
    fr: {
      skip: 'Passer au contenu principal',
      heading: 'Mot de passe oublié ?',
      intro: "Entrez l'adresse courriel utilisée pour votre compte. Si elle correspond à un compte existant, nous vous enverrons un code de vérification pour réinitialiser votre mot de passe.",
      email: 'Adresse courriel',
      submit: 'Envoyer le code de vérification',
      successHeadline: "Si un compte correspondant à cette adresse existe, un code de vérification a été envoyé.",
      successDetail: 'Lorsque vous recevrez le code, vous pourrez passer à la page de réinitialisation à l’aide du bouton ci-dessous.',
      continue: 'Passer à la réinitialisation du mot de passe',
      error: 'Une erreur est survenue. Veuillez réessayer plus tard.'
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await ensureCaptcha();
      await axios.post('/api/auth/forgot-password', {
        email: email.trim(),
        requestRoute: '/forgot-password',
        requestFlow: 'forgot-password'
      });
      setSubmitted(true);
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage || text[language].error);
    }
  };

  if (submitted) {
    const resetUrl = `/reset-password${email ? `?email=${encodeURIComponent(email.trim())}` : ''}`;
    return (
      <>
        <CaptchaOverlay />
        <a href="#main-content" className="govuk-skip-link">{text[language].skip}</a>
        <div
          role="status"
          tabIndex={-1}
          className="govuk-panel govuk-panel--confirmation success-message"
          ref={successRef}
          id="main-content"
        >
          <p className="govuk-panel__title">{text[language].successHeadline}</p>
          <p className="govuk-body">{text[language].successDetail}</p>
        </div>
        <p className="govuk-body">
          <Link className="govuk-button" to={resetUrl}>{text[language].continue}</Link>
        </p>
      </>
    );
  }

  return (
    <>
      <CaptchaOverlay />
      <a href="#main-content" className="govuk-skip-link">{text[language].skip}</a>
      <main className="govuk-main-wrapper govuk-width-container" role="main" id="main-content" tabIndex="-1">
        <h1 className="govuk-heading-l">{text[language].heading}</h1>
        <p className="govuk-hint">{text[language].intro}</p>
        <form
          onSubmit={handleSubmit}
          aria-label={language === 'fr' ? 'Formulaire de mot de passe oublié' : 'Forgot password form'}
          className="govuk-form-group forgot-password-form"
          autoComplete="on"
        >
          <label className="govuk-label" htmlFor="forgot-email">{text[language].email}</label>
          <input
            className="govuk-input"
            id="forgot-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            aria-label={text[language].email}
          />
          {/* Add space between input and button */}
          <div style={{ marginTop: '1.5em' }} />
          {error && (
            <div
              className="govuk-error-message error-message"
              role="alert"
              aria-live="assertive"
              tabIndex="-1"
              ref={errorRef}
            >
              {error}
            </div>
          )}
          <button type="submit" className="govuk-button">{text[language].submit}</button>
        </form>
      </main>
    </>
  );
}

export default ForgotPasswordRequest;
